import { Box, CircularProgress, Divider, Stack } from "@mui/material";
import { RichTreeViewPro, RichTreeViewProSlotProps, useTreeViewApiRef } from "@mui/x-tree-view-pro";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExpandCollapseToggle } from "@sinch/components/application/Rate/RateMigration/components/ExpandCollapseToggle";
import { RateMigrationRow } from "@sinch/components/application/Rate/RateMigration/components/RateMigrationRow";
import { SelectAllCheckbox } from "@sinch/components/application/Rate/RateMigration/components/SelectAllCheckbox";
import {
  CaptionBold,
  LeftControls,
  RightCaption,
} from "@sinch/components/application/Rate/RateMigration/components/styled";
import { RatelistNameFragment } from "@sinch/components/application/Rate/RateMigration/queries.types";
import { ExtendedTreeItemProps } from "@sinch/components/application/Rate/RateMigration/types";
import { useMigrationSelection } from "@sinch/components/application/Rate/RateMigration/UseRateMigrationSelection";

export interface RateMigrationProps {
  onChange: (arg: { jobIds: string[]; positionIds: string[]; transportTripIds: string[] }) => void;
  loading: boolean;
  tree: ExtendedTreeItemProps[];
  targetRatelist: RatelistNameFragment | null | undefined;
}

export const RateMigration: FC<RateMigrationProps> = ({ onChange, loading, targetRatelist, tree }) => {
  const { t } = useTranslation(["common", "draft"]);
  const apiRef = useTreeViewApiRef();
  const [expanded, setExpanded] = useState<string[]>([]);

  const { onSelectedChange, handleSelectedItemsChange, selected } = useMigrationSelection({
    onChange,
    apiRef,
  });

  const handleExpandedItemsChange = useCallback<(event: React.SyntheticEvent | null, itemIds: string[]) => void>(
    (_, expandedItems) => setExpanded(expandedItems),
    []
  );

  return (
    <Box>
      <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3} sx={{ pb: 1 }} width="100%">
        <LeftControls alignItems="center" direction="row" spacing={1}>
          <SelectAllCheckbox apiRef={apiRef} onSelectedChange={onSelectedChange} />
          <ExpandCollapseToggle apiRef={apiRef} onExpandedChange={setExpanded} />
          <CaptionBold variant="caption">{t("Job.label")}</CaptionBold>
        </LeftControls>
        <RightCaption variant="caption">{t("RateMigration.targetRatelist")}</RightCaption>
      </Stack>
      <Divider />
      {loading && (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="secondary" size={30} sx={{ m: 2 }} />
        </Box>
      )}
      {!loading && (
        <RichTreeViewPro<ExtendedTreeItemProps, true>
          apiRef={apiRef}
          checkboxSelection
          expandedItems={expanded}
          itemChildrenIndentation={8}
          items={tree}
          multiSelect
          onExpandedItemsChange={handleExpandedItemsChange}
          onSelectedItemsChange={handleSelectedItemsChange}
          selectedItems={selected}
          selectionPropagation={{
            descendants: true,
            parents: true,
          }}
          slotProps={
            { item: { targetRatelist: targetRatelist } } as RichTreeViewProSlotProps<ExtendedTreeItemProps, true>
          }
          slots={{ item: RateMigrationRow as any }}
        />
      )}
    </Box>
  );
};
